/**
 * 画面の状態 (タブ・選択・絞り込み) を URL のクエリ (?key=value) に持たせるフック (2026-10-30)。
 *
 * 商談詳細などへ移ってから「前の画面へ」/ ブラウザの戻るで戻った時やリロードした時に、
 * 状態が元のまま開くようにするため。
 * - 書き換えは replace (履歴を増やさない)。他のクエリ (Hub の ?hub= など) と location.state は残す
 * - 既定値にした時はクエリから消す (URL を短く保つ)
 * - Hub の中の画面どうしでもぶつからないキー名にする (Hub は ?hub= を使う)
 * - 1 回の操作で書き換えるキーは 1 つにする (同時に 2 回呼ぶと後の呼び出しが前を上書きする)
 */
import { useCallback } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';

export function useUrlParam(key: string): [string | null, (value: string | null) => void] {
  const [searchParams, setSearchParams] = useSearchParams();
  const { state } = useLocation();
  const value = searchParams.get(key);

  const setValue = useCallback(
    (next: string | null) => {
      setSearchParams(
        (prev) => {
          const p = new URLSearchParams(prev);
          if (next === null || next === '') p.delete(key);
          else p.set(key, next);
          return p;
        },
        { replace: true, state },
      );
    },
    [key, setSearchParams, state],
  );

  return [value, setValue];
}

/** 決まった値のどれかを取るクエリ。URL の値が不正・無しなら fallback。 */
export function useUrlEnumParam<T extends string>(
  key: string,
  allowed: readonly T[],
  fallback: T,
): [T, (value: T) => void] {
  const [raw, setRaw] = useUrlParam(key);
  const value =
    raw !== null && (allowed as readonly string[]).includes(raw) ? (raw as T) : fallback;
  const setValue = useCallback(
    (next: T) => setRaw(next === fallback ? null : next),
    [setRaw, fallback],
  );
  return [value, setValue];
}

/** ON / OFF のクエリ (ON の時だけ ?key=1)。 */
export function useUrlFlagParam(key: string): [boolean, (value: boolean) => void] {
  const [raw, setRaw] = useUrlParam(key);
  const setValue = useCallback((next: boolean) => setRaw(next ? '1' : null), [setRaw]);
  return [raw === '1', setValue];
}
